<template>
  <v-card-text>
    <v-row
      :class="{
        'grey lighten-5': !$vuetify.theme.isDark,
        black: $vuetify.theme.isDark,
      }"
      class="rounded-lg"
    >
      <v-col cols="12" sm="4">
        <v-card flat class="ma-2">
          <v-card-text>
            <h3>
              Stock taking info.
              <v-btn
                :dark="!item.locked"
                :disabled="item.locked"
                rounded
                depressed
                text
                small
                color="orange"
                @click="showEditDialog = true"
                >Edit
              </v-btn>
              <v-btn
                :dark="!item.locked"
                :disabled="item.locked"
                rounded
                depressed
                text
                color="red"
                small
                @click="showDeleteRecord = true"
                >Delete
              </v-btn>
              <v-btn
                @click="showLockDialog = true"
                :disabled="item.locked"
                text
                color="red"
                :dark="!item.locked"
                rounded
                depressed
                small
                >Lock
              </v-btn>
            </h3>
            <strong>Description:</strong>
            <p class="mb-1">{{ item.description }}</p>

            <strong>Created At:</strong>
            <p class="mb-1">{{ item.created_at | humanDateTime }}</p>

            <strong>Created By:</strong>
            <p>{{ item.user_name }}</p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card flat class="ma-2">
          <v-card-text>
            <h3>Stock taking Items Summary.</h3>
            <strong>Total Items Count:</strong>
            <p class="mb-1">{{ summaries.total_items_count }}</p>

            <strong>Recorded</strong>
            <p class="mb-1">
              {{ summaries.total_recorded_items_count }}
              <v-progress-linear
                color="green"
                rounded
                :value="recordedPercent"
              ></v-progress-linear>
            </p>

            <strong>Remaining</strong>
            <p class="mb-2 mb-4">
              {{ summaries.total_not_recorded_items_count }}
              <v-progress-linear
                color="red"
                :value="notRecordedPercent"
              ></v-progress-linear>
            </p>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card flat class="ma-2">
          <v-card-text>
            <v-btn
              :dark="!item.locked"
              :disabled="item.locked"
              @click="
                getMissingItems();
                showAddItemDialog = true;
              "
              class="mt-3"
              block
              color="orange"
              rounded
              depressed
              x-large
              >Add Item
            </v-btn>

            <v-btn
              :disabled="item.locked"
              block
              color="green"
              :dark="!item.locked"
              rounded
              depressed
              outlined
              class="mt-3"
              x-large
              @click="showSyncDialog = true"
              >Sync All Items
            </v-btn>
            <v-btn
              :href="downloadURL"
              block
              color="orange"
              dark
              rounded
              depressed
              outlined
              class="mt-3 mb-4"
              x-large
              >Download PDF</v-btn
            >
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-progress-linear
          v-if="saving"
          indeterminate
          color="orange"
        ></v-progress-linear>
        <v-skeleton-loader type="table" v-if="loading"></v-skeleton-loader>
        <v-simple-table v-else-if="items" fixed-header height="80vh">
          <thead>
            <tr>
              <th>item</th>
              <th>Code</th>
              <th class="text-center">Quantity</th>
              <th class="text-center">Quantity on shelf</th>
              <th class="text-center">Variance</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(recordItem, index) in items.data" :key="index">
              <td>
                <v-avatar rounded="lg" class="ma-2">
                  <v-img :src="recordItem.item.photo"></v-img>
                </v-avatar>
                <v-btn
                  text
                  color="orange"
                  small
                  depressed
                  class="pa-0"
                  :to="'/inventory/view/' + recordItem.item.id"
                  target="_blank"
                  >{{ recordItem.item.name
                  }}<sup>
                    <v-icon x-small>mdi-open-in-new</v-icon>
                  </sup></v-btn
                >
              </td>
              <td>{{ recordItem.item.code }}</td>
              <td class="text-center">
                {{ recordItem.local_quantity }}
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      @click="synItem(recordItem, index)"
                      icon
                      small
                      v-on="on"
                      v-if="
                        recordItem.item.quantity != recordItem.local_quantity &&
                        !item.locked
                      "
                    >
                      <v-icon color="warning" small>mdi-alert-outline</v-icon>
                    </v-btn>
                  </template>
                  <span class="white--text">
                    <strong class="d-block">Click to sync this item:</strong>
                    The current quantity of this item is
                    {{ recordItem.item.quantity }} <br />
                    and not the same as the quantity captured here,<br />
                    consider re-syncing this record to reflect your current
                    <br />
                    inventory and avoid buying or selling during the stock
                    taking process.
                  </span>
                </v-tooltip>
              </td>

              <td class="text-center">
                <v-text-field
                  class="mx-auto text-center"
                  style="width: 160px; text-align: center !important"
                  type="number"
                  min="0"
                  outlined
                  v-if="!recordItem.recorded && !item.locked"
                  v-model="recordItem.shelf_quantity"
                  dense
                >
                  <template v-slot:append>
                    <v-btn
                      @click="
                        recordTaking(recordItem, index);
                        recordItem.recorded = true;
                      "
                      color="green"
                      icon
                    >
                      <v-icon color="green">mdi-check</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
                <span v-else>{{ recordItem.shelf_quantity }}</span>
              </td>
              <td class="text-center">
                <span
                  :class="{
                    'green--text':
                      recordItem.local_quantity -
                        Number(recordItem.shelf_quantity) ===
                      0,
                    'red--text':
                      recordItem.local_quantity -
                        Number(recordItem.shelf_quantity) !=
                      0,
                  }"
                >
                  {{
                    recordItem.local_quantity -
                    Number(recordItem.shelf_quantity)
                  }}
                </span>
              </td>

              <td>
                {{ recordItem.created_at | humanDateTime }}
              </td>

              <td>
                {{ recordItem.updated_at | humanDateTime }}
              </td>

              <td>
                <template v-if="!item.locked">
                  <span v-if="!recordItem.recorded">
                    <v-btn
                      @click="
                        recordTaking(recordItem, index);
                        recordItem.recorded = true;
                      "
                      text
                      rounded
                      depressed
                      color="green"
                      >Save<v-icon color="green" small>mdi-check</v-icon></v-btn
                    >
                  </span>
                  <span v-else>
                    <v-menu transition="scale-transition">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          :disabled="
                            recordItem.item.quantity !=
                            recordItem.local_quantity
                          "
                          v-on="on"
                          icon
                          color="orange"
                          ><v-icon>mdi-dots-horizontal</v-icon></v-btn
                        >
                      </template>
                      <v-list>
                        <v-list-item
                          @click="
                            items.data[index].recorded =
                              !items.data[index].recorded
                          "
                        >
                          <v-list-item-title>
                            Change Shelf Quantity
                          </v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          v-if="
                            recordItem.local_quantity -
                              Number(recordItem.shelf_quantity) !=
                            0
                          "
                          @click="
                            selectedItem = recordItem;
                            showAdjustDialog = true;
                          "
                        >
                          <v-list-item-title>
                            <span
                              v-if="
                                recordItem.local_quantity -
                                  Number(recordItem.shelf_quantity) <
                                0
                              "
                            >
                              Add
                              {{
                                Math.abs(
                                  recordItem.local_quantity -
                                    Number(recordItem.shelf_quantity)
                                )
                              }}
                              to inventory
                            </span>

                            <span
                              v-if="
                                recordItem.local_quantity -
                                  Number(recordItem.shelf_quantity) >
                                0
                              "
                            >
                              Write-off
                              {{
                                Math.abs(
                                  recordItem.local_quantity -
                                    Number(recordItem.shelf_quantity)
                                )
                              }}
                              from inventory
                            </span>
                          </v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          @click="
                            showDeleteItemDialog = true;
                            selectedItem = recordItem;
                          "
                        >
                          <v-list-item-title class="red--text">
                            Remove from list
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </span>
                </template>
                <template v-else>
                  <v-icon color="grey">mdi-lock-outline</v-icon>
                </template>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <pagination-component
          v-if="items"
          @goto="(page) => getItems(page)"
          :links="items.links"
        ></pagination-component>
      </v-col>
    </v-row>

    <v-dialog width="500" v-model="showEditDialog" :persistent="saving">
      <v-card>
        <v-card-title>Edit Stock taking record</v-card-title>
        <v-card-text>
          <v-form ref="editStockForm">
            <v-textarea
              :rules="$store.state.requiredRule"
              outlined
              auto-grow
              v-model="description"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="saveChanges"
            :loading="saving"
            block
            depressed
            dark
            color="orange"
            rounded
            x-large
            >Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="showDeleteItemDialog" :persistent="saving">
      <v-card>
        <v-card-title>Delete stock taking item</v-card-title>
        <v-card-text>
          <h2 class="red--text">
            Do you want to remove this item from this record?
          </h2>
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="removeItem"
            :loading="saving"
            color="red"
            dark
            block
            x-large
            rounded
            depressed
            >Yes, Remove Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="showAddItemDialog" persistent>
      <v-card>
        <v-card-title>
          Add a missing item
          <v-spacer></v-spacer>
          <v-btn
            depressed
            rounded
            color="red lighten-5 red--text"
            @click="showAddItemDialog = false"
            >close
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-skeleton-loader
            type="list-item@8"
            v-if="loadingItems"
          ></v-skeleton-loader>
          <v-list v-else-if="missingItems && missingItems.length > 0">
            <v-list-item
              v-for="item in missingItems"
              :key="item"
              @click="addMissingItem(item)"
            >
              <v-list-item-avatar rounded="lg">
                <v-img :src="item.photo"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle
                  >Code:{{ item.code }}</v-list-item-subtitle
                >
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <norecords-component
            v-else
            title="No Items"
            description="You have no other items to add to this stock taking record"
          ></norecords-component>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="showLockDialog">
      <v-card>
        <v-card-title>Confirm lock</v-card-title>
        <v-card-text>
          <h2 class="red--text">
            Are you sure you want to lock this record permanently?
          </h2>
          <p>
            You will not be able to make changes to this record after this
            action. You will not be able to unlock this record
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="lockRecord"
            :loading="saving"
            block
            rounded
            depressed
            x-large
            color="red"
            dark
            >Yes, Lock This Record
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteRecord" width="500" :persistent="saving">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          <h2 class="red--text">
            Are you sure you want to delete this stock taking record
          </h2>
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="deleteRecord"
            :loading="saving"
            block
            color="red"
            dark
            depressed
            x-large
            rounded
            >Yes, Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="showSyncDialog" :persistent="loading">
      <v-card>
        <v-card-title>Confirm sync</v-card-title>
        <v-card-text>
          <h3>Do you want to sync all items in this stock taking record?</h3>
          <p>
            We will find all items who's quantities are not same as the
            quantities on record and sync them with this record.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            block
            dark
            color="orange"
            rounded
            depressed
            x-large
            @click="SyncAll"
            :loading="loading"
            >Sync All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="showAdjustDialog">
      <adjust-component
        :default-step="2"
        @saved="getItems()"
        read-only
        :stock-takingitem-id="selectedItem.id"
        :default-mode="
          selectedItem.local_quantity - Number(selectedItem.shelf_quantity) > 0
            ? 'damage'
            : 'purchase'
        "
        :default-type="
          selectedItem.local_quantity - Number(selectedItem.shelf_quantity) > 0
            ? 'decrease'
            : 'increase'
        "
        :default-quantity="
          Math.abs(
            selectedItem.local_quantity - Number(selectedItem.shelf_quantity)
          )
        "
        :title="
          selectedItem.local_quantity - Number(selectedItem.shelf_quantity) > 0
            ? 'Write-off inventory'
            : 'Add to inventory'
        "
        v-if="selectedItem"
        @closed="showAdjustDialog = false"
        :item="selectedItem.item"
      ></adjust-component>
    </v-dialog>
  </v-card-text>
</template>

<script>
import NorecordsComponent from "./noRecordComponent.vue";
import AdjustComponent from "./AdjustComponent.vue";
import PaginationComponent from "./PaginationComponent.vue";

export default {
  name: "StockTakingItemDetailsComponent",
  components: { PaginationComponent, AdjustComponent, NorecordsComponent },
  emits: ["updated", "deleted"],
  props: {
    item: {
      type: Object,
    },
  },
  data() {
    return {
      showSyncDialog: false,
      showLockDialog: false,
      loading: false,
      items: null,
      saving: false,
      showEditDialog: false,
      description: "",
      selectedItem: null,
      showDeleteItemDialog: false,
      showAddItemDialog: false,
      loadingItems: false,
      missingItems: [],
      showAdjustDialog: false,
      showDeleteRecord: false,
      summaries: {
        total_items_count: 0,
        total_recorded_items_count: 0,
        total_not_recorded_items_count: 0,
      },
    };
  },
  computed: {
    downloadURL() {
      return (
        this.$store.state.baseUrl +
        "/stock-taking/" +
        this.item.uuid +
        "/download?enid=" +
        this.$store.state.user.current_business.enc_id
      );
    },

    recordedPercent() {
      return this.summaries.total_items_count > 0
        ? (this.summaries.total_recorded_items_count /
            this.summaries.total_items_count) *
            100
        : 0;
    },
    notRecordedPercent() {
      return this.summaries.total_items_count > 0
        ? (this.summaries.total_not_recorded_items_count /
            this.summaries.total_items_count) *
            100
        : 0;
    },
  },
  watch: {
    item() {
      if (this.items) {
        this.getSummaries();
        this.getItems();
        this.description = this.item.description;
      }
    },
  },
  methods: {
    SyncAll() {
      this.loading = true;
      const URL = "/api/stock-taking/" + this.item.uuid + "/sync-items";
      axios.get(URL).then((res) => {
        this.loading = false;
        this.getItems();
        this.showSyncDialog = false;
      });
    },
    synItem(item, index) {
      this.saving = true;

      const URL = "/api/stock-taking/items/" + item.id + "/sync";
      axios.get(URL).then((res) => {
        this.items.data[index] = res.data;
        this.saving = false;
      });
    },

    deleteRecord() {
      this.saving = true;

      const URL = "/api/stock-taking/" + this.item.uuid;

      axios.delete(URL).then((res) => {
        this.$emit("deleted", res.data);
        this.saving = false;
      });
    },

    lockRecord() {
      this.saving = true;
      const URL = "/api/stock-taking/" + this.item.uuid + "/lock";
      axios.get(URL).then((res) => {
        this.saving = false;
        this.$emit("updated", res.data);
        this.showLockDialog = false;
      });
    },

    addMissingItem(item) {
      const data = {
        item_id: item.id,
      };

      const URL = "/api/stock-taking/" + this.item.uuid + "/add-item";

      axios.post(URL, data).then((res) => {
        this.showAddItemDialog = false;
        this.items.data.unshift(res.data);
        this.getSummaries();
      });
    },
    getMissingItems() {
      this.loadingItems = false;

      const URL = "/api/stock-taking/" + this.item.uuid + "/missing-items";

      axios.get(URL).then((res) => {
        this.missingItems = res.data;
        this.loadingItems = false;
      });
    },

    removeItem() {
      this.saving = true;

      const URL = "/api/stock-taking/items/" + this.selectedItem.id;

      axios.delete(URL).then((res) => {
        this.getItems();
        this.getSummaries();
        this.saving = false;
        this.showDeleteItemDialog = false;
      });
    },

    saveChanges() {
      if (this.$refs.editStockForm.validate()) {
        this.saving = true;

        const Data = {
          description: this.description,
        };

        const URL = "/api/stock-taking/" + this.item.uuid;
        axios.post(URL, Data).then((res) => {
          this.saving = false;
          this.showEditDialog = false;
          this.$emit("updated", res.data);
        });
      }
    },

    getItems(page = 1) {
      this.loading = true;
      const URL = "/api/stock-taking/" + this.item.uuid + "/items?page=" + page;

      axios.get(URL).then((res) => {
        this.items = res.data;
        this.loading = false;
      });
    },
    recordTaking(item, index) {
      this.saving = true;
      const data = {
        shelf_quantity: item.shelf_quantity,
      };

      const URL = "/api/stock-taking/items/" + item.id + "/record";

      axios.post(URL, data).then((res) => {
        this.items.data[index] = res.data;

        this.saving = false;
        this.getSummaries();
      });
    },
    getSummaries() {
      const URL = "/api/stock-taking/" + this.item.uuid + "/summaries";

      axios.get(URL).then((res) => {
        this.summaries = res.data;
      });
    },
  },
  mounted() {
    this.description = this.item.description;
    this.getItems();
    this.getSummaries();
  },
};
</script>
